import { textField } from './components/form/textField'
import { TypographyStyle, createTheme } from '@mui/material/styles'
import { colors } from './components/base/colors'
import { TypeFromArray } from 'types/helpers'
import { buttons } from './components/buttons/root'
import { ObjEntries } from 'utils/helpers'
import { CSSProperties } from '@mui/material/styles/createTypography'
import { formLabel } from './components/form/formLabel'
import { autocomplete } from './components/form/autocomplete'
import { Capacitor } from '@capacitor/core'

const isMobilePlatform =
  Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android'

declare module '@mui/material/styles' {
  interface Palette {
    white: {
      main: string
    }
    gradient: {
      success: string
      error: string
      warning: string
      info: string
    }
  }
  interface PaletteOptions {
    white?: {
      main: string
    }
    gradient?: {
      success: string
      error: string
      warning: string
      info: string
    }
  }

  interface TypeBackground {
    dark: string
  }
  interface TypographyVariants {
    body1_regular: CSSProperties
    body1_medium: CSSProperties
    body1_semibold: CSSProperties

    h3_regular: CSSProperties
    h3_medium: CSSProperties
    h3_bold: CSSProperties
    h5_regular: CSSProperties
    h5_medium: CSSProperties
    caption1_regular: CSSProperties
    caption1_medium: CSSProperties
    caption2_semibold: CSSProperties
    caption1_bold: CSSProperties
    caption2_regular: CSSProperties
    caption3_bold: CSSProperties
    caption3_regular: CSSProperties
    caption4: CSSProperties
    body1_bold: CSSProperties
    caption2_medium: CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    h3_regular: CSSProperties
    h3_medium: CSSProperties
    h3_bold: CSSProperties
    h5_regular: CSSProperties
    h5_medium: CSSProperties
    caption1_regular: CSSProperties
    caption1_medium: CSSProperties
    caption1_semibold: CSSProperties
    caption2_semibold: CSSProperties
    caption3_bold: CSSProperties
    caption1_bold: CSSProperties
    body1_regular?: CSSProperties
    body1_medium?: CSSProperties
    body1_semibold?: CSSProperties
    caption3_regular?: CSSProperties
    caption2_regular?: CSSProperties
    caption4?: CSSProperties
    body1_bold?: CSSProperties
    caption2_medium?: CSSProperties
  }

  interface Theme {
    customVariables: {
      safeAreaInsetTop: string
      safeAreaInsetBottom: string
      safeAreaInsetLeft: string
      safeAreaInsetRight: string
      keyboardSafeAreaInsetBottom: string
    }
  }

  interface ThemeOptions {
    customVariables?: {
      safeAreaInsetTop?: string
      safeAreaInsetBottom?: string
      safeAreaInsetLeft?: string
      safeAreaInsetRight?: string
      keyboardSafeAreaInsetBottom?: string
    }
  }
}

declare module '@mui/material/styles/createTypography' {
  interface TypographyOptions extends TextVariants {
    body1?: undefined
    body2?: undefined
    body3?: undefined
    body4?: undefined
    caption1?: undefined
    caption2?: undefined
    caption3?: undefined
  }
  interface Typography extends TextVariants {
    body1?: undefined
    body2?: undefined
    body3?: undefined
    body4?: undefined
    caption1?: undefined
    caption2?: undefined
    caption3?: undefined
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1_regular: true
    body1_medium: true
    body1_semibold: true
    h3_regular: true
    h3_medium: true
    h3_bold: true
    h5_regular: true
    h5_medium: true
    caption1_regular: true
    caption1_medium: true
    caption1_semibold: true
    caption2_semibold: true
    caption1_bold: true
    caption2_regular: true
    caption3_bold: true
    caption3_regular: true
    caption4: true
    body1_bold: true
    caption2_medium: true
  }
}
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxxs: true
    xxs: true
    st: true
    xxl: true
  }
}

export const textVariantsList = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'h3_regular',
  'h3_medium',
  'h3_semibold',
  'h3_bold',
  'h4_bold',
  'h5_regular',
  'h5_medium',
  'h5_semibold',
  'body1_bold',
  'body1_semibold',
  'body1_medium',
  'body1_regular',
  'body1_underline',
  'caption1_regular',
  'caption1_medium',
  'caption1_semibold',
  'caption1_bold',
  'caption2_regular',
  'caption2_medium',
  'caption2_semibold',
  'caption2_bold',
  'caption3_regular',
  'caption3_bold',
  'caption4',
  'button'
] as const

export type TextVariants = {
  [V in TypeFromArray<typeof textVariantsList>]: TypographyStyle
}

const defaultStyles: TypographyStyle = {
  marginTop: 0,
  marginBottom: 0,
  fontFamily: 'Montserrat, sans-serif'
}

export const typographyStyles: TextVariants = {
  h1: {
    fontSize: '35px',
    fontWeight: 600
  },
  h2: {
    fontSize: '29px',
    fontWeight: 600
  },
  h3: {
    fontSize: '24px',
    fontWeight: 600
  },
  h4: {
    fontSize: '20px',
    fontWeight: 600
  },
  h5: {
    fontSize: '18px',
    fontWeight: 600
  },
  h6: {
    fontSize: '16px',
    fontWeight: 600
  },
  h3_regular: {
    fontSize: '24px',
    fontWeight: 300
  },
  h3_medium: {
    fontSize: '24px',
    fontWeight: 500
  },
  h3_semibold: {
    fontSize: '24px',
    fontWeight: 600
  },
  h3_bold: {
    fontSize: '24px',
    fontWeight: 700
  },
  h4_bold: {
    fontSize: '20px',
    fontWeight: 700
  },
  h5_regular: {
    fontSize: '18px',
    fontWeight: 400
  },
  h5_medium: {
    fontSize: '18px',
    fontWeight: 500
  },
  h5_semibold: {
    fontSize: '18px',
    fontWeight: 600
  },
  body1_bold: {
    fontSize: '16px',
    fontWeight: 700
  },
  body1_semibold: {
    fontSize: '16px',
    fontWeight: 600
  },
  body1_medium: {
    fontSize: '16px',
    fontWeight: 500
  },
  body1_regular: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px'
  },
  body1_underline: {
    fontSize: '16px',
    fontWeight: 700,
    textDecoration: 'underline',
    lineHeight: '24px'
  },
  caption1_regular: {
    fontSize: '12px',
    fontWeight: 400
  },
  caption1_medium: {
    fontSize: '12px',
    fontWeight: 500
  },
  caption1_semibold: {
    fontSize: '12px',
    fontWeight: 600
  },
  caption1_bold: {
    fontSize: '12px',
    fontWeight: 700
  },
  caption2_regular: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px'
  },
  caption2_medium: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px'
  },
  caption2_semibold: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px'
  },
  caption2_bold: {
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '20px'
  },
  caption3_regular: {
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '20px'
  },
  caption3_bold: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '20px'
  },
  button: {
    fontSize: '14px',
    fontWeight: 700,
    color: colors.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  },
  caption4: {
    fontSize: '10px',
    fontWeight: 600
  }
}

export const typography = ObjEntries(typographyStyles).reduce<TextVariants>(
  (acc, [v, styles]) => ({
    ...acc,
    [v]: {
      ...(v === 'button' ? {} : defaultStyles),
      ...styles
    }
  }),
  {} as TextVariants
)

export const theme = createTheme({
  palette: { ...colors },
  breakpoints: {
    values: {
      xxxs: 0,
      xxs: 390,
      xs: 600,
      sm: 767,
      md: 900,
      st: 1080,
      lg: 1200,
      xl: 1352,
      xxl: 1536
    }
  },
  typography: {
    ['allVariants']: {
      color: colors.text.primary
    },
    fontFamily: [
      'Montserrat',
      'sans-serif',
      'Roboto',
      'Helvetica',
      'Arial'
    ].join(','),
    ...typography
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          '@media (max-width:767px)': {
            backgroundColor: colors.white.main
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          ['& .MuiMenu-paper']: {
            border: `1px solid ${colors.grey[200]}`,
            borderRadius: 0,
            boxShadow: 'none'
          },
          ['& .MuiList-root']: {
            padding: 0
          }
        }
      }
    },
    MuiButton: { ...buttons },
    MuiTextField: {
      ...textField
    },
    MuiFormLabel: {
      ...formLabel
    },
    MuiAutocomplete: {
      ...autocomplete
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          ['& .MuiSvgIcon-root']: {
            color: colors.info.main
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          ['&:hover']: {
            backgroundColor: 'transparent'
          },
          ['& .MuiTouchRipple-root']: {
            display: 'none'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 0
        },
        select: {
          padding: '8px 12px'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          border: `1px solid ${colors.grey[200]}`
        }
      }
    },

    MuiTableCell: {
      styleOverrides: {
        body: {
          borderRight: `1px solid ${colors.grey[100]}`,
          padding: '4px 8px',
          fontSize: '14px',
          position: 'relative',
          ['&:last-child']: {
            borderRight: 'none'
          }
        },
        head: {
          backgroundColor: colors.white.main,
          borderRight: `1px solid ${colors.grey[200]}`,
          fontWeight: 'bold',
          lineHeight: '1.1',
          padding: '12px 18px 12px 12px',
          fontSize: '14px',
          ['&:last-child']: {
            borderRight: 'none'
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          ['&:nth-of-type(even)']: {
            backgroundColor: colors.grey[100]
          }
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          ['&:hover']: {
            backgroundColor: 'transparent',
            border: `2px solid ${colors.info.main}`
          },
          ['&.Mui-selected']: {
            backgroundColor: 'transparent',
            border: `2px solid ${colors.info.main}`
          },
          ['&.Mui-selected:hover']: {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.white.main,
          border: `1px solid ${colors.grey[300]}`,
          color: colors.text.primary,
          ['& .MuiTooltip-arrow']: {
            color: colors.grey[300]
          }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
          height: '16px',
          overflow: 'visible',
          ['& .MuiSwitch-thumb']: {
            border: `1px solid${colors.grey[300]}`
          },
          ['& .Mui-checked']: {
            border: 'none',
            color: colors.white.main
          },
          ['& .MuiSwitch-switchBase']: {
            padding: 0,
            marginLeft: '10px',
            bottom: '2px'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 0
        }
      }
    }
  },

  customVariables: {
    safeAreaInsetTop: 'env(safe-area-inset-top)',
    safeAreaInsetBottom: 'env(safe-area-inset-bottom)',
    safeAreaInsetLeft: 'env(safe-area-inset-left)',
    safeAreaInsetRight: 'env(safe-area-inset-right)',
    keyboardSafeAreaInsetBottom: 'env(safe-area-inset-bottom)'
  }
})

theme.typography = {
  ...theme.typography,
  h2: {
    ...theme.typography.h2,
    [theme.breakpoints.down('xl')]: {
      fontSize: '22px',
      lineHeight: '22px'
    }
  },
  h3: {
    ...theme.typography.h3,
    [theme.breakpoints.down('xl')]: {
      fontSize: '20px'
    }
  },
  h4: {
    ...theme.typography.h4,
    [theme.breakpoints.down('xl')]: {
      fontSize: '18px'
    }
  },
  body1_regular: {
    ...theme.typography.body1_regular,
    [theme.breakpoints.down('xl')]: {
      fontSize: '14px',
      lineHeight: '16px'
    }
  },
  h5: {
    ...theme.typography.h5,
    [theme.breakpoints.down('xl')]: {
      fontSize: '16px'
    }
  },
  h6: {
    ...theme.typography.h6,
    [theme.breakpoints.down('xl')]: {
      fontSize: '14px'
    }
  },
  body1_medium: {
    ...theme.typography.body1_medium,
    [theme.breakpoints.down('xl')]: {
      fontSize: '14px'
    }
  },

  h3_regular: {
    ...theme.typography.h3_regular,
    [theme.breakpoints.down('xl')]: {
      fontSize: '22px',
      lineHeight: '22px'
    }
  }
}
